import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  UserPlus, 
  Trash2, 
  Users,
  Mail,
  Phone,
  User,
  Plus,
  X,
  Clock,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Gift,
  MessageSquare,
  Equal,
  SlidersHorizontal,
  AlertTriangle,
  CreditCard,
  Package,
  MapPin,
  Truck,
  ShoppingCart
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency, getCurrencySymbol, type Currency } from "@/lib/currency";
import { GST_MODULE_ENABLED } from "@/lib/featureFlags";
import { getAppliedSplitDiscount } from "@/lib/splitDiscount";
import { supabase } from "@/integrations/supabase/client";
import { ShippingAddressForm } from "@/components/ShippingAddressForm";
import { buildMerchantStoreUrl, buildPublicAppUrl } from "@/lib/appConfig";
// Cart store removed - split payments now work via URL params only
import { useCartStore } from "@/stores/cartStore";
import { RecurringReminderDialog } from "@/components/RecurringReminderDialog";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionErrors";

interface Participant {
  id: string;
  name: string;
  mobile: string;
  email: string;
  customAmount?: number; // For custom split type
}

interface ShippingAddress {
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  zip: string;
  phone: string;
}

interface PendingRequest {
  id: string;
  session_token: string;
  merchant_id: string;
  shopify_draft_order_id: string;
  variant_id: string;
  product_title: string;
  product_image: string | null;
  quantity: number;
  status: string;
  subtotal: number;
  total_tax: number;
  total_shipping: number;
  total_price: number;
  customer_email: string | null;
  shipping_address: any;
  cart_attributes?: Record<string, unknown> | null;
  selector_context?: Record<string, unknown> | null;
  participant_count: number | null;
  cart_items?: Array<{
    title?: string;
    image?: string | null;
    quantity?: number;
    price?: number;
    variantTitle?: string;
  }> | null;
}

interface SplitCartItemSnapshot {
  title?: string;
  image?: string | null;
  quantity?: number;
  price?: number;
  variantTitle?: string;
}

interface SplitCartSnapshot {
  items: SplitCartItemSnapshot[];
  subtotal: number;
  currency?: Currency;
}

interface MerchantSettings {
  store_name: string;
  store_domain: string;
  default_deadline_hours: number;
  cancellation_fee_type: string;
  cancellation_fee_value: number;
  currency: Currency;
  minimum_split_order_value?: number;
  split_discount_enabled?: boolean;
  split_discount_percentage?: number | null;
  split_discount_cap?: number | null;
}

const SPLIT_CART_SNAPSHOT_PREFIX = "splitkart-cart-snapshot:";
const SPLIT_CART_LATEST_SNAPSHOT_KEY = "splitkart-latest-cart-snapshot";

function normalizeSplitCartItems(items: any[] | null | undefined): SplitCartItemSnapshot[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      title: item?.title || item?.name || "Product",
      image: item?.image || item?.productImage || item?.product_image || null,
      quantity: Number(item?.quantity || item?.qty || 1),
      price: Number(item?.price || item?.linePrice || item?.line_total || 0),
      variantTitle: item?.variantTitle || item?.variant_title || "",
    }))
    .filter((item) => item.title && item.quantity > 0);
}

function calculateSnapshotSubtotal(items: SplitCartItemSnapshot[], explicitSubtotal?: number | null): number {
  const subtotal = Number(explicitSubtotal || 0);
  if (Number.isFinite(subtotal) && subtotal > 0) return subtotal;
  return items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0);
}

function calculateItemsDisplayTotal(items: SplitCartItemSnapshot[] | null | undefined): number {
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
    0,
  );
}

function buildCartSnapshotFromParams(params: URLSearchParams): SplitCartSnapshot | null {
  const cartDataParam = params.get("cartData");
  if (!cartDataParam) return null;

  try {
    const items = normalizeSplitCartItems(JSON.parse(cartDataParam));
    if (!items.length) return null;

    const subtotal = calculateSnapshotSubtotal(items, Number(params.get("cartTotal") || 0));
    return {
      items,
      subtotal,
      currency: (params.get("currency") || "INR") as Currency,
    };
  } catch {
    return null;
  }
}

function parseCartAttributesFromParams(params: URLSearchParams): Record<string, unknown> {
  const cartAttributesParam = params.get("cartAttributes");
  if (!cartAttributesParam) return {};

  try {
    const parsed = JSON.parse(cartAttributesParam);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function parseSelectorContextFromParams(params: URLSearchParams): Record<string, unknown> {
  const selectorContextParam = params.get("selectorContext");
  if (!selectorContextParam) return {};

  try {
    const parsed = JSON.parse(selectorContextParam);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function buildCartSnapshotFromPending(
  pendingRequest: PendingRequest | null | undefined,
  currency?: Currency,
): SplitCartSnapshot | null {
  if (!pendingRequest) return null;

  const items = normalizeSplitCartItems(pendingRequest.cart_items || []);
  const fallbackItems = items.length
    ? items
    : normalizeSplitCartItems([
        {
          title: pendingRequest.product_title,
          image: pendingRequest.product_image,
          quantity: pendingRequest.quantity || 1,
          price: Number(pendingRequest.subtotal || pendingRequest.total_price || 0) / Math.max(1, Number(pendingRequest.quantity || 1)),
        },
      ]);
  const subtotal = calculateSnapshotSubtotal(fallbackItems, Number(pendingRequest.subtotal || pendingRequest.total_price || 0));

  if (!fallbackItems.length || subtotal <= 0) return null;
  return { items: fallbackItems, subtotal, currency };
}

function getCartSnapshotStorageKey(sessionToken: string): string {
  return `${SPLIT_CART_SNAPSHOT_PREFIX}${sessionToken}`;
}

function readCartSnapshot(sessionToken: string | null | undefined): SplitCartSnapshot | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionToken
      ? sessionStorage.getItem(getCartSnapshotStorageKey(sessionToken)) ||
        localStorage.getItem(getCartSnapshotStorageKey(sessionToken)) ||
        sessionStorage.getItem(SPLIT_CART_LATEST_SNAPSHOT_KEY) ||
        localStorage.getItem(SPLIT_CART_LATEST_SNAPSHOT_KEY)
      : sessionStorage.getItem(SPLIT_CART_LATEST_SNAPSHOT_KEY) ||
        localStorage.getItem(SPLIT_CART_LATEST_SNAPSHOT_KEY);
    if (!raw) return null;
    const snapshot = JSON.parse(raw) as SplitCartSnapshot;
    const items = normalizeSplitCartItems(snapshot.items);
    const subtotal = calculateSnapshotSubtotal(items, Number(snapshot.subtotal || 0));
    if (!items.length || subtotal <= 0) return null;
    return { items, subtotal, currency: snapshot.currency };
  } catch {
    return null;
  }
}

function writeCartSnapshot(sessionToken: string | null | undefined, snapshot: SplitCartSnapshot | null): void {
  if (!snapshot || typeof window === "undefined") return;

  const items = normalizeSplitCartItems(snapshot.items);
  const subtotal = calculateSnapshotSubtotal(items, snapshot.subtotal);
  if (!items.length || subtotal <= 0) return;

  const payload = JSON.stringify({ ...snapshot, items, subtotal });
  sessionStorage.setItem(SPLIT_CART_LATEST_SNAPSHOT_KEY, payload);
  localStorage.setItem(SPLIT_CART_LATEST_SNAPSHOT_KEY, payload);

  if (sessionToken) {
    sessionStorage.setItem(getCartSnapshotStorageKey(sessionToken), payload);
    localStorage.setItem(getCartSnapshotStorageKey(sessionToken), payload);
  }
}

function mergePendingRequestWithSnapshot(
  pendingRequest: PendingRequest,
  snapshot: SplitCartSnapshot | null,
): PendingRequest {
  if (!snapshot) return pendingRequest;

  const currentItems = normalizeSplitCartItems(pendingRequest.cart_items || []);
  const currentSubtotal = Number(pendingRequest.subtotal || 0);
  const currentTotal = Number(pendingRequest.total_price || 0);
  const shouldUseSnapshot = snapshot.subtotal > 0 && (currentSubtotal <= 0 || !currentItems.length);

  if (!shouldUseSnapshot) return pendingRequest;

  const firstItem = snapshot.items[0];
  return {
    ...pendingRequest,
    cart_items: snapshot.items,
    subtotal: snapshot.subtotal,
    total_price: currentTotal > 0 ? currentTotal : snapshot.subtotal,
    product_title: pendingRequest.product_title || firstItem?.title || "Product",
    product_image: pendingRequest.product_image || firstItem?.image || null,
    quantity: pendingRequest.quantity || snapshot.items.reduce((sum, item) => sum + Number(item.quantity || 1), 0),
  };
}

// NOTE: Using hoisted function declarations to prevent TDZ errors in production builds
function getDeadlineLabel(hours: number): string {
  if (hours <= 12) return "12 Hours";
  if (hours <= 24) return "1 Day";
  if (hours <= 48) return "2 Days";
  if (hours <= 72) return "3 Days";
  if (hours <= 120) return "5 Days";
  if (hours <= 168) return "7 Days";
  return "14 Days";
}

// Get initial participant count from localStorage or URL params
// MUST be defined OUTSIDE component to avoid "Cannot access before initialization" error
function getInitialParticipantCount(participantsParam: string | null): number {
  // First check localStorage for pending session data
  try {
    const pendingSession = localStorage.getItem('shakatah-pending-session');
    if (pendingSession) {
      const { participantCount } = JSON.parse(pendingSession);
      if (participantCount && participantCount > 1) {
        return Math.max(1, participantCount - 1); // -1 because participants array excludes organizer
      }
    }
  } catch (e) {
    console.error("Error reading localStorage:", e);
  }
  // Fallback to URL param
  if (participantsParam) {
    return Math.max(1, Number(participantsParam) - 1);
  }
  return 1; // Default to 1 friend
}

function isLikelySplitSessionToken(value: string | null): boolean {
  if (!value) {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function getSplitSessionToken(searchParams: URLSearchParams): string | null {
  const explicitToken = searchParams.get("token")?.trim() || null;
  if (explicitToken) {
    return explicitToken;
  }

  const legacySessionToken = searchParams.get("session")?.trim() || null;
  if (isLikelySplitSessionToken(legacySessionToken)) {
    return legacySessionToken;
  }

  return null;
}

function buildPublicSplitSearchParams(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();
  const allowedKeys = [
    "token",
    "participants",
    "cartData",
    "cartTotal",
    "currency",
    "source",
    "store",
    "merchantId",
    "merchantToken",
    "platform",
  ];

  allowedKeys.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      params.set(key, value);
    }
  });

  if (!params.get("token")) {
    const legacySessionToken = searchParams.get("session");
    if (isLikelySplitSessionToken(legacySessionToken)) {
      params.set("token", legacySessionToken!);
    }
  }

  return params;
}

function isAdminEmbeddedSplitLaunch(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.location.hostname === "admin.shopify.com") {
    return true;
  }

  if (window.location.search.includes("embedded=1")) {
    return true;
  }

  try {
    return window.top !== window.self;
  } catch (error) {
    return true;
  }
}

function redirectSplitFlowToPublicApp() {
  if (typeof window === "undefined") {
    return;
  }

  const currentParams = new URLSearchParams(window.location.search);
  const sanitizedParams = buildPublicSplitSearchParams(currentParams);
  const search = sanitizedParams.toString();
  const publicUrl = buildPublicAppUrl(`/shakatah-split${search ? `?${search}` : ""}`);

  try {
    if (window.top && window.top.location) {
      window.top.location.href = publicUrl;
      return;
    }
  } catch (error) {
    // Fall through to same-window navigation if top-level access is blocked.
  }

  window.location.href = publicUrl;
}

function redirectToMerchantStore(storeUrl: string | null | undefined, navigate: ReturnType<typeof useNavigate>) {
  const merchantStoreUrl = buildMerchantStoreUrl(storeUrl);
  if (merchantStoreUrl) {
    window.location.href = merchantStoreUrl;
    return;
  }

  navigate("/");
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function ShakatahSplitSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  // Clear cart function - now just clears localStorage markers
  const clearShakatahCart = () => {
    try {
      localStorage.removeItem('shakatah-split-cart');
      localStorage.removeItem('shakatah-checkout-in-progress');
      localStorage.removeItem('shakatah-split-completed');
      sessionStorage.removeItem('shakatah-checkout-completed-this-session');
    } catch (e) {
      console.error('Error clearing cart markers:', e);
    }
  };
  const regularCartItems = useCartStore((state) => state.items);
  const regularCartHasItems = regularCartItems.length > 0;
  
  const sessionToken = getSplitSessionToken(searchParams);
  const participantsParam = searchParams.get('participants');
  const cartDataParam = searchParams.get('cartData');
  const storeDomain = searchParams.get('store') || '';
  const merchantIdParam = searchParams.get('merchantId') || searchParams.get('merchantToken') || '';
  const platformParam = searchParams.get('platform') || '';
  const cartTotalParam = searchParams.get('cartTotal') || '';
  
  // State for managing session token (can be set after creating draft order)
  const [activeSessionToken, setActiveSessionToken] = useState<string | null>(sessionToken);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const sessionCreationRef = useRef(!!cartDataParam); // Track if we arrived with cart data (session will be created)
  
  const [loading, setLoading] = useState(!cartDataParam); // Don't show loading if we have cart data to render immediately
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const [merchantSettings, setMerchantSettings] = useState<MerchantSettings | null>(null);
  const [step, setStep] = useState<"address" | "friends" | "payment" | "awaiting_payment" | "complete">("address");
  
  // Address validation state
  const [validatedAddress, setValidatedAddress] = useState<ShippingAddress | null>(null);
  const [validatedPricing, setValidatedPricing] = useState<{ subtotal: number; tax: number; shipping: number; total: number } | null>(null);
  const [shippingWarning, setShippingWarning] = useState<string | null>(null);
  
  // Gift option state
  const [isGift, setIsGift] = useState(false);
  const [giftRecipientName, setGiftRecipientName] = useState("");
  const [organizerGiftMessage, setOrganizerGiftMessage] = useState("");
  
  // Cancellation fee agreement
  const [agreedToCancellationFee, setAgreedToCancellationFee] = useState(false);
  
  // Split type: equal or custom
  const [splitType, setSplitType] = useState<'equal' | 'custom'>('equal');
  const [organizerCustomAmount, setOrganizerCustomAmount] = useState<number | undefined>(undefined);
  
  // Request ID after creation (for organizer payment)
  const [requestId, setRequestId] = useState<string | null>(null);
  const [organizerParticipantId, setOrganizerParticipantId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Recurring reminder dialog state
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  
  // Organizer details (from Shopify checkout)
  const [organizerDetails, setOrganizerDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  
  // Friends/participants for split payment (NOT including organizer)
  const [participants, setParticipants] = useState<Participant[]>(() => {
    const count = getInitialParticipantCount(participantsParam);
    return Array.from({ length: count }, (_, i) => ({
      id: String(i + 1),
      name: "",
      mobile: "",
      email: "",
    }));
  });

  // Checkout URL for organizer (Shopify checkout)
  const [organizerCheckoutUrl, setOrganizerCheckoutUrl] = useState<string | null>(null);

  const shouldUsePublicParticipantPayment = () => (
    platformParam.toLowerCase() === "woocommerce" ||
    Boolean(pendingRequest?.shopify_draft_order_id?.startsWith("woo-"))
  );

  const openOrganizerParticipantPayment = (paymentRequestId: string) => {
    window.location.href = buildPublicAppUrl(
      `/split/${paymentRequestId}?email=${encodeURIComponent(organizerDetails.email)}`,
    );
  };

  useEffect(() => {
    if (!isAdminEmbeddedSplitLaunch()) {
      return;
    }

    redirectSplitFlowToPublicApp();
  }, []);

  // Create draft order from cart data if no session token
  useEffect(() => {
    if (activeSessionToken || !cartDataParam || isCreatingSession) return;
    
    const createDraftOrderFromCart = async () => {
      setIsCreatingSession(true);
      try {
        const urlCartSnapshot = buildCartSnapshotFromParams(searchParams);
        writeCartSnapshot(null, urlCartSnapshot);

        const items = JSON.parse(cartDataParam);
        if (!items?.length) {
          toast({
            title: "Cart is empty",
            description: "Please add items to your cart before proceeding",
            variant: "destructive",
          });
          return;
        }

        const lineItems = items.map((item: { variantId: string; quantity: number; title: string; image?: string; price?: number }) => ({
          variantId: item.variantId,
          quantity: item.quantity,
          title: item.title,
          image: item.image || '',
          price: Number(item.price || 0),
        }));

        const { data, error } = await supabase.functions.invoke('create-draft-order', {
          body: {
            storeDomain,
            merchantId: merchantIdParam || undefined,
            platform: platformParam || undefined,
            cartTotal: cartTotalParam ? Number(cartTotalParam) : undefined,
            cartAttributes: parseCartAttributesFromParams(searchParams),
            selectorContext: parseSelectorContextFromParams(searchParams),
            lineItems,
            participantCount: 2,
          },
        });

        if (error) throw error;

        if (data?.sessionToken) {
          const responsePendingRequest = data.pendingRequest as PendingRequest | undefined;
          const responseCurrency = (data.merchantSettings?.currency || searchParams.get('currency') || "INR") as Currency;
          const responseSnapshot =
            buildCartSnapshotFromPending(responsePendingRequest, responseCurrency) ||
            buildCartSnapshotFromParams(searchParams);

          writeCartSnapshot(data.sessionToken, responseSnapshot);

          // Use returned data directly to skip extra fetches
          if (responsePendingRequest) {
            setPendingRequest(mergePendingRequestWithSnapshot(responsePendingRequest, responseSnapshot));
          }
          if (data.merchantSettings) {
            setMerchantSettings({
              store_name: data.merchantSettings.store_name,
              store_domain: data.merchantSettings.store_domain,
              default_deadline_hours: data.merchantSettings.default_deadline_hours,
              cancellation_fee_type: data.merchantSettings.cancellation_fee_type,
              cancellation_fee_value: data.merchantSettings.cancellation_fee_value,
              currency: (data.merchantSettings.currency || "INR") as Currency,
              minimum_split_order_value: Number(data.merchantSettings.minimum_split_order_value || 0),
              split_discount_enabled: Boolean(data.merchantSettings.split_discount_enabled),
              split_discount_percentage: data.merchantSettings.split_discount_percentage ?? null,
              split_discount_cap: data.merchantSettings.split_discount_cap ?? null,
            });
            setLoading(false);
          }
          setActiveSessionToken(data.sessionToken);
          // Clean URL params
          const newParams = new URLSearchParams();
          newParams.set('token', data.sessionToken);
          if (participantsParam) newParams.set('participants', participantsParam);
          if (cartDataParam) newParams.set('cartData', cartDataParam);
          if (cartTotalParam) newParams.set('cartTotal', cartTotalParam);
          if (platformParam) newParams.set('platform', platformParam);
          if (searchParams.get('currency')) newParams.set('currency', searchParams.get('currency')!);
          if (searchParams.get('cartAttributes')) newParams.set('cartAttributes', searchParams.get('cartAttributes')!);
          if (searchParams.get('selectorContext')) newParams.set('selectorContext', searchParams.get('selectorContext')!);
          if (storeDomain) newParams.set('store', storeDomain);

          const nextPath = `/shakatah-split?${newParams.toString()}`;
          if (isAdminEmbeddedSplitLaunch()) {
            const publicUrl = buildPublicAppUrl(nextPath);

            try {
              if (window.top && window.top.location) {
                window.top.location.href = publicUrl;
                return;
              }
            } catch (topLevelError) {
              // Fall back to same-window navigation below.
            }

            window.location.href = publicUrl;
            return;
          }

          navigate(nextPath, { replace: true });
        } else {
          throw new Error("Failed to create session");
        }
      } catch (error: unknown) {
        console.error("Error creating draft order:", error);
        const errorMessage = await getSupabaseFunctionErrorMessage(
          error,
          "Please try again from the store",
        );
        toast({
          title: "Failed to start split payment",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsCreatingSession(false);
      }
    };

    createDraftOrderFromCart();
  }, [activeSessionToken, cartDataParam, isCreatingSession, storeDomain, merchantIdParam, platformParam, cartTotalParam, participantsParam, navigate, toast]);

  // Fetch pending request and subscribe to realtime updates
  useEffect(() => {
    if (!activeSessionToken) {
      if (!cartDataParam && !isCreatingSession && !sessionCreationRef.current) {
        toast({
          title: "Invalid session",
          description: "No session token found. Please start over from the product page.",
          variant: "destructive",
        });
      }
      return;
    }
    // Session is active, clear the ref
    sessionCreationRef.current = false;

    const fetchPendingRequest = async () => {
      // Skip fetch if data was already loaded from create-draft-order response
      if (pendingRequest && merchantSettings) {
        setLoading(false);
        return;
      }
      setLoading(true);
      
      const { data, error } = await supabase
        .from("pending_split_requests")
        .select("*")
        .eq("session_token", activeSessionToken)
        .maybeSingle();

      console.log("Pending request result:", { data, error, sessionToken: activeSessionToken });

      if (error) {
        console.error("Supabase error fetching pending request:", error);
        toast({
          title: "Error loading session",
          description: `Database error: ${error.message}`,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      
      if (!data) {
        console.error("No data found for session token:", activeSessionToken);
        toast({
          title: "Session not found",
          description: "Your split payment session has expired or is invalid. Please start over from the product page.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const pendingData = data as PendingRequest;
      const storedSnapshot =
        readCartSnapshot(activeSessionToken) ||
        buildCartSnapshotFromParams(searchParams) ||
        buildCartSnapshotFromPending(pendingData);
      const mergedPendingRequest = mergePendingRequestWithSnapshot(pendingData, storedSnapshot);
      writeCartSnapshot(activeSessionToken, buildCartSnapshotFromPending(mergedPendingRequest, storedSnapshot?.currency) || storedSnapshot);

      // Set the pending request data without allowing an incomplete DB row to wipe out the cart summary.
      setPendingRequest(mergedPendingRequest);

      // Set organizer details from pending request
      if (data.customer_email) {
        setOrganizerDetails(prev => ({ ...prev, email: data.customer_email }));
      }
      
      // Check if address is already set (status is 'ready')
      if (data.status === 'ready' && data.shipping_address) {
        // Skip to friends step if address already validated
        setStep("friends");
        
        // Set validated pricing from stored data
        setValidatedPricing({
          subtotal: data.subtotal || 0,
          tax: data.total_tax || 0,
          shipping: data.total_shipping || 0,
          total: data.total_price || 0,
        });
      }
      
      // Parse shipping address for name
      if (data.shipping_address) {
        try {
          const addr = typeof data.shipping_address === 'string' 
            ? JSON.parse(data.shipping_address) 
            : data.shipping_address;
          if (addr.first_name || addr.name) {
            const parsedFirstName = addr.first_name || addr.name?.split(' ')[0] || '';
            const parsedLastName = addr.last_name || addr.name?.split(' ').slice(1).join(' ') || '';
            setOrganizerDetails(prev => ({
              ...prev,
              firstName: parsedFirstName,
              lastName: parsedLastName,
            }));
          }
          if (addr.phone) {
            setOrganizerDetails(prev => ({ ...prev, phone: addr.phone }));
          }
        } catch (e) {
          console.error("Error parsing shipping address:", e);
        }
      }

      // Set participant count from database if available
      if (data.participant_count && data.participant_count > 1) {
        const friendCount = data.participant_count - 1; // -1 because array excludes organizer
        setParticipants(prev => {
          // Only update if current count doesn't match
          if (prev.length !== friendCount) {
            return Array.from({ length: friendCount }, (_, i) => ({
              id: String(Date.now() + i),
              name: prev[i]?.name || "",
              mobile: prev[i]?.mobile || "",
              email: prev[i]?.email || "",
            }));
          }
          return prev;
        });
      }

      // Fetch merchant settings using public function (bypasses RLS)
      if (data.merchant_id) {
        console.log("Fetching merchant settings for:", data.merchant_id);
        const { data: merchantData, error: merchantError } = await supabase
          .rpc("get_merchant_public_settings", { _merchant_id: data.merchant_id });
        
        console.log("Merchant settings result:", { merchantData, merchantError });
        
        if (merchantData && merchantData.length > 0) {
          const merchant = merchantData[0];
          setMerchantSettings({
            store_name: merchant.store_name,
            store_domain: merchant.store_domain,
            default_deadline_hours: merchant.default_deadline_hours ?? 72,
            cancellation_fee_type: merchant.cancellation_fee_type ?? "fixed",
            cancellation_fee_value: merchant.cancellation_fee_value ?? 100,
            currency: (merchant.currency as Currency) ?? "USD",
            minimum_split_order_value: Number((merchant as any).minimum_split_order_value || 0),
            split_discount_enabled: Boolean((merchant as any).split_discount_enabled),
            split_discount_percentage: (merchant as any).split_discount_percentage ?? null,
            split_discount_cap: (merchant as any).split_discount_cap ?? null,
          });
        } else {
          console.warn("No merchant settings found, using defaults");
          // Set default values if merchant not found
          setMerchantSettings({
            store_name: "Store",
            store_domain: "",
            default_deadline_hours: 72,
            cancellation_fee_type: "fixed",
            cancellation_fee_value: 100,
            currency: "USD",
            minimum_split_order_value: 0,
            split_discount_enabled: false,
            split_discount_percentage: null,
            split_discount_cap: null,
          });
        }
      }

      setLoading(false);
    };

    fetchPendingRequest();

    // Subscribe to realtime updates for this pending request
    const channel = supabase
      .channel(`pending-request-${activeSessionToken}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pending_split_requests',
          filter: `session_token=eq.${activeSessionToken}`,
        },
        (payload) => {
          console.log("Pending request updated:", payload);
          const updatedPendingRequest = payload.new as unknown as PendingRequest;
          const storedSnapshot =
            readCartSnapshot(activeSessionToken) ||
            buildCartSnapshotFromParams(searchParams) ||
            buildCartSnapshotFromPending(updatedPendingRequest);
          const mergedPendingRequest = mergePendingRequestWithSnapshot(updatedPendingRequest, storedSnapshot);
          writeCartSnapshot(activeSessionToken, buildCartSnapshotFromPending(mergedPendingRequest, storedSnapshot?.currency) || storedSnapshot);
          setPendingRequest(mergedPendingRequest);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeSessionToken, toast, cartDataParam, isCreatingSession, searchParams]);

  const cartSnapshot =
    readCartSnapshot(activeSessionToken) ||
    readCartSnapshot(null) ||
    buildCartSnapshotFromParams(searchParams) ||
    buildCartSnapshotFromPending(pendingRequest, merchantSettings?.currency);
  const displayCurrency = merchantSettings?.currency ?? cartSnapshot?.currency ?? ((searchParams.get('currency') as Currency | null) || "INR");
  const fallbackCartItems = cartSnapshot?.items?.length ? cartSnapshot.items : null;

  const pendingCartItems = pendingRequest?.cart_items?.filter((item) => item?.title) || [];
  const pendingSubtotal = Number(pendingRequest?.subtotal || 0);
  const urlSubtotal = Number(searchParams.get('cartTotal') || 0);
  const summaryItems = pendingCartItems.length ? pendingCartItems : fallbackCartItems;
  const summaryItemsTotal = calculateItemsDisplayTotal(summaryItems);
  const summarySubtotal = pendingSubtotal > 0
    ? pendingSubtotal
    : urlSubtotal > 0
      ? urlSubtotal
      : cartSnapshot?.subtotal && cartSnapshot.subtotal > 0
        ? cartSnapshot.subtotal
        : summaryItemsTotal;

  // Use validated pricing if available, otherwise fall back to pending request/URL cart data
  const activePricing = validatedPricing || {
    subtotal: summarySubtotal,
    tax: pendingRequest?.total_tax || 0,
    shipping: pendingRequest?.total_shipping || 0,
    total: pendingRequest?.total_price && Number(pendingRequest.total_price) > 0 ? Number(pendingRequest.total_price) : summarySubtotal,
  };
  const originalSubtotal = summaryItemsTotal > 0 ? summaryItemsTotal : summarySubtotal;
  const appliedSplitDiscount = getAppliedSplitDiscount(originalSubtotal, {
    enabled: merchantSettings?.split_discount_enabled,
    percentage: merchantSettings?.split_discount_percentage,
    cap: merchantSettings?.split_discount_cap,
  });
  const preValidationItemsTotal = summaryItemsTotal > 0
    ? summaryItemsTotal
    : pendingRequest?.total_price && Number(pendingRequest.total_price) > 0
      ? Number(pendingRequest.total_price)
      : summarySubtotal;
  const inferredTaxMode = validatedPricing && validatedPricing.tax > 0
    ? Math.abs(
        validatedPricing.total -
        (validatedPricing.subtotal + validatedPricing.shipping)
      ) < 0.01
      ? "inclusive"
      : Math.abs(
          validatedPricing.total -
          (validatedPricing.subtotal + validatedPricing.tax + validatedPricing.shipping)
        ) < 0.01
        ? "exclusive"
        : null
    : null;
  const taxModeBadgeLabel = inferredTaxMode === "inclusive"
    ? "Tax Inclusive"
    : inferredTaxMode === "exclusive"
      ? "Tax Exclusive"
      : "Tax Finalized At Checkout";
  
  // Calculate split amounts
  const totalPrice = activePricing.total;
  const totalParticipants = participants.length + 1; // +1 for organizer
  const equalSplitAmount = totalPrice / totalParticipants;
  
  // For display purposes - in custom mode, use organizerCustomAmount or equal split
  const organizerAmount = splitType === 'custom' && organizerCustomAmount !== undefined 
    ? organizerCustomAmount 
    : equalSplitAmount;
  
  // Legacy: Keep splitAmount for backward compatibility in some places
  const splitAmount = organizerAmount;
  
  // Calculate total of custom amounts (for validation)
  const customAmountsTotal = splitType === 'custom'
    ? (organizerCustomAmount || 0) + participants.reduce((sum, p) => sum + (p.customAmount || 0), 0)
    : totalPrice;
  
  // Check if custom split amounts are valid
  const isCustomSplitValid = splitType === 'equal' || (
    splitType === 'custom' && 
    Math.abs(customAmountsTotal - totalPrice) < 0.01 && // Allow for floating point errors
    organizerCustomAmount !== undefined && organizerCustomAmount > 0 &&
    participants.every(p => p.customAmount !== undefined && p.customAmount > 0)
  );
  
  // Handle address validation callback
  const handleAddressValidated = (
    address: ShippingAddress, 
    pricing: { subtotal: number; tax: number; shipping: number; total: number },
    warning?: string | null,
    organizer?: { firstName: string; lastName: string; email: string; phone: string }
  ) => {
    setValidatedAddress(address);
    setValidatedPricing(pricing);
    if (warning) {
      setShippingWarning(warning);
    }
    
    // Update organizer details from the form if provided
    if (organizer) {
      setOrganizerDetails(prev => ({
        ...prev,
        firstName: organizer.firstName,
        lastName: organizer.lastName,
        email: organizer.email,
        phone: organizer.phone,
      }));
    }
    
    // Move to friends step and scroll to top
    setStep("friends");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };
  
  // Calculate cancellation fee display - uses merchant settings when available
  const getCancellationFeeDisplay = (): string => {
    // Use merchant settings if available, otherwise use 1% default
    const feeType = merchantSettings?.cancellation_fee_type ?? 'percentage';
    const feeValue = merchantSettings?.cancellation_fee_value ?? 1;
    const currency = merchantSettings?.currency ?? "USD";
    
    if (!merchantSettings) return "1%";
    
    if (feeType === 'percentage') {
      const feeAmount = (totalPrice * feeValue) / 100;
      return `${feeValue}% (${formatCurrency(feeAmount, currency)})`;
    }
    return formatCurrency(feeValue, currency);
  };

  // Check for duplicate phone numbers (including organizer)
  const getDuplicatePhones = (): string[] => {
    const allPhones = [
      organizerDetails.phone.trim(),
      ...participants.map(p => p.mobile.trim())
    ].filter(phone => phone !== "");
    
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    
    for (const phone of allPhones) {
      // Normalize phone number for comparison (remove spaces, dashes)
      const normalized = phone.replace(/[\s\-]/g, "");
      if (seen.has(normalized)) {
        duplicates.add(phone);
      }
      seen.add(normalized);
    }
    
    return Array.from(duplicates);
  };
  
  const hasDuplicatePhones = (): boolean => {
    return getDuplicatePhones().length > 0;
  };

  // Check for duplicate emails (including organizer)
  const getDuplicateEmails = (): string[] => {
    const allEmails = [
      organizerDetails.email.trim().toLowerCase(),
      ...participants.map(p => p.email.trim().toLowerCase())
    ].filter(email => email !== "");
    
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    
    for (const email of allEmails) {
      if (seen.has(email)) {
        duplicates.add(email);
      }
      seen.add(email);
    }
    
    return Array.from(duplicates);
  };
  
  const hasDuplicateEmails = (): boolean => {
    return getDuplicateEmails().length > 0;
  };

  const isParticipantsValid = () => {
    const allFieldsValid = participants.every(
      (p) =>
        p.name.trim() !== "" &&
        p.email.trim() !== "" &&
        p.email.includes("@")
    );
    
    return allFieldsValid && !hasDuplicatePhones() && !hasDuplicateEmails();
  };

  const isOrganizerValid = () => {
    return (
      organizerDetails.firstName.trim() !== "" &&
      organizerDetails.lastName.trim() !== "" &&
      organizerDetails.email.trim() !== "" &&
      organizerDetails.email.includes("@") &&
      organizerDetails.phone.trim() !== ""
    );
  };

  const addParticipant = () => {
    if (participants.length >= 19) {
      toast({
        title: "Maximum participants reached",
        description: "You can split payment with up to 20 people total",
        variant: "destructive",
      });
      return;
    }
    setParticipants([
      ...participants,
      { id: Date.now().toString(), name: "", mobile: "", email: "" },
    ]);
  };

  // Set exact number of participants
  const setParticipantCount = (count: number) => {
    const currentCount = participants.length;
    if (count === currentCount) return;
    
    if (count > currentCount) {
      // Add more participants
      const newParticipants = [...participants];
      for (let i = currentCount; i < count; i++) {
        newParticipants.push({ id: Date.now().toString() + i, name: "", mobile: "", email: "" });
      }
      setParticipants(newParticipants);
    } else {
      // Remove participants from the end
      setParticipants(participants.slice(0, count));
    }
  };

  const removeParticipant = (id: string) => {
    if (participants.length <= 1) {
      toast({
        title: "Minimum participants required",
        description: "You need at least 1 friend to split the payment",
        variant: "destructive",
      });
      return;
    }
    setParticipants(participants.filter((p) => p.id !== id));
  };

  const updateParticipant = (id: string, field: "name" | "mobile" | "email" | "customAmount", value: string | number) => {
    setParticipants(
      participants.map((p) => {
        if (p.id !== id) return p;
        if (field === "customAmount") {
          return { ...p, customAmount: typeof value === 'number' ? value : parseFloat(value) || undefined };
        }
        return { ...p, [field]: value };
      })
    );
  };

  // Step 1: Create the split payment request
  const handleCreateRequest = async () => {
    if (!pendingRequest) return;
    
    if (!isOrganizerValid()) {
      toast({
        title: "Incomplete details",
        description: "Please fill in your name and phone number",
        variant: "destructive",
      });
      return;
    }
    
    if (!isParticipantsValid()) {
      toast({
        title: "Incomplete details",
        description: "Please fill in all participant details correctly",
        variant: "destructive",
      });
      return;
    }
    
    // Validate custom split amounts
    if (splitType === 'custom' && !isCustomSplitValid) {
      toast({
        title: "Invalid split amounts",
        description: `Custom amounts must total ${formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}. Current total: ${formatCurrency(customAmountsTotal, merchantSettings?.currency ?? "USD")}`,
        variant: "destructive",
      });
      return;
    }

    if (!agreedToCancellationFee) {
      toast({
        title: "Agreement required",
        description: "Please agree to the split payment and cancellation terms to proceed",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    
    try {
      // Map participants to include custom amounts if applicable
      const participantsList = participants.map(p => ({
        name: p.name,
        email: p.email,
        mobile: p.mobile,
        customAmount: splitType === 'custom' ? p.customAmount : undefined,
      }));

      // Get store domain from merchant using RPC to bypass RLS
      let storeDomain = '';
      if (pendingRequest.merchant_id) {
        const { data: merchantSettings } = await supabase
          .rpc('get_merchant_public_settings', { _merchant_id: pendingRequest.merchant_id });
        
        if (merchantSettings && merchantSettings.length > 0) {
          storeDomain = merchantSettings[0].store_domain || '';
        }
      }

      // Determine the organizer's split amount
      const organizerSplitAmount = splitType === 'custom' ? organizerCustomAmount : equalSplitAmount;

      const { data, error } = await supabase.functions.invoke('send-split-payment', {
        body: {
          productName: pendingRequest.product_title,
          totalPrice: pendingRequest.total_price,
          splitAmount: organizerSplitAmount, // Organizer's share
          splitType, // 'equal' or 'custom'
          participants: participantsList,
          customerName: `${organizerDetails.firstName} ${organizerDetails.lastName}`.trim(),
          customerEmail: organizerDetails.email,
          customerPhone: organizerDetails.phone,
          isGift,
          giftRecipientName: isGift ? giftRecipientName : null,
          organizerGiftMessage: isGift ? organizerGiftMessage : null,
          organizerMessage: organizerDetails.message || null,
          productImageUrl: pendingRequest.product_image,
          productDescription: null,
          variantId: pendingRequest.variant_id,
          storeDomain,
          shopifyDraftOrderId: pendingRequest.shopify_draft_order_id,
          shippingAddress: pendingRequest.shipping_address,
          taxAmount: pendingRequest.total_tax,
          shippingAmount: pendingRequest.total_shipping,
          cartItems: pendingRequest.cart_items,
          cartAttributes: pendingRequest.cart_attributes,
          organizerPaysFirst: true,
          merchantId: pendingRequest.merchant_id, // Pass merchant ID directly
        },
      });

      if (error) throw error;

      // Split was successfully created - clear the cart immediately
      // CRITICAL: Set markers BEFORE clearing cart to block any writes during state change
      localStorage.removeItem('shakatah-pending-session');
      localStorage.setItem('shakatah-split-completed', 'true');
      localStorage.setItem('shakatah-checkout-in-progress', 'true');
      localStorage.setItem('shakatah-checkout-timestamp', String(Date.now())); // Timestamp for expiration
      sessionStorage.setItem('shakatah-checkout-completed-this-session', 'true');
      
      // Also clear localStorage cart BEFORE Zustand state change
      // This prevents persist middleware from reading/writing stale data
      try {
        localStorage.removeItem('shakatah-split-cart');
        // Clear widget cart keys too
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('shakatah-split-cart-')) {
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
        console.error('Error clearing cart localStorage:', e);
      }
      
      // NOW clear Zustand state (markers are already set, so writes will be blocked)
      clearShakatahCart();

      setRequestId(data.requestId);
      setOrganizerParticipantId(data.organizerParticipantId);
      
      // Switch to awaiting payment step immediately
      setStep("awaiting_payment");
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });

      // WooCommerce payments use the connected merchant payment provider rather
      // than Shopify draft-order checkout.
      if (shouldUsePublicParticipantPayment()) {
        openOrganizerParticipantPayment(data.requestId);
        return;
      }

      // Then create checkout and open in new tab
      try {
        const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-participant-checkout', {
          body: {
            requestId: data.requestId,
            participantId: data.organizerParticipantId,
          },
        });

        if (checkoutError) throw checkoutError;

        if (!checkoutData?.checkoutUrl) {
          throw new Error("Failed to create checkout - no URL returned");
        }

        setOrganizerCheckoutUrl(checkoutData.checkoutUrl);

        toast({
          title: "Payment page opened",
          description: "Complete your payment in the new tab. This page will update automatically.",
        });

        // Redirect to checkout in same tab (avoids popup blockers)
        window.location.href = checkoutData.checkoutUrl;
        
      } catch (checkoutError: any) {
        console.error("Error creating checkout:", checkoutError);
        // Fallback: show payment step if checkout creation fails
        setStep("payment");
        toast({
          title: "Almost there!",
          description: "Click the button to complete your payment",
        });
      }

    } catch (error: any) {
      console.error("Error creating request:", error);
      const errorMessage = await getSupabaseFunctionErrorMessage(error, "Please try again");
      toast({
        title: "Failed to create split request",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  // Handle organizer payment - creates Shopify checkout and redirects
  const handleOrganizerPayment = async () => {
    if (!requestId || !organizerParticipantId) {
      toast({
        title: "Error",
        description: "Payment session not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingPayment(true);

    try {
      if (shouldUsePublicParticipantPayment()) {
        openOrganizerParticipantPayment(requestId);
        return;
      }

      // Create Shopify checkout for organizer's share
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-participant-checkout', {
        body: {
          requestId: requestId,
          participantId: organizerParticipantId,
        },
      });

      if (checkoutError) throw checkoutError;

      if (!checkoutData?.checkoutUrl) {
        throw new Error("Failed to create checkout - no URL returned");
      }

      // Store the checkout URL
      setOrganizerCheckoutUrl(checkoutData.checkoutUrl);

      // Clean up ALL session and cart storage
      localStorage.removeItem('shakatah_session');
      localStorage.removeItem('shakatah-pending-session');
      
      // CRITICAL: Set checkout markers BEFORE clearing cart
      // These markers tell the system to block rehydration on next page load
      localStorage.setItem('shakatah-checkout-in-progress', 'true');
      localStorage.setItem('shakatah-split-completed', 'true');
      localStorage.setItem('shakatah-checkout-timestamp', String(Date.now())); // Timestamp for expiration
      
      // ALSO set sessionStorage flag for same-session protection
      // This survives page refreshes within the same browser session
      sessionStorage.setItem('shakatah-checkout-completed-this-session', 'true');
      
      // Clear the session storage hash to prevent stale cart imports
      sessionStorage.removeItem('shakatah-imported-cart-hash');
      
      // Clear ALL cart-related localStorage keys
      try {
        // Main cart storage
        localStorage.removeItem('shakatah-split-cart');
        
        // Also clear any widget-specific cart keys (format: shakatah-split-cart-[domain])
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('shakatah-split-cart-')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          console.log('Clearing widget cart key:', key);
          localStorage.removeItem(key);
        });
        
        console.log('All shakatah cart localStorage cleared before checkout');
      } catch (e) {
        console.error('Failed to clear shakatah cart from localStorage:', e);
      }
      
      // Clear the Zustand state
      clearShakatahCart();

      toast({
        title: "Checkout ready!",
        description: "Opening merchant checkout page...",
      });

      // Redirect to checkout in same tab (avoids popup blockers)
      window.location.href = checkoutData.checkoutUrl;

    } catch (error: any) {
      console.error("Error creating checkout:", error);
      const errorMessage = await getSupabaseFunctionErrorMessage(error, "Please try again");
      toast({
        title: "Checkout failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
          <h1 className="text-2xl font-bold">Loading your order...</h1>
          <p className="text-muted-foreground">
            Please wait while we retrieve your order details
          </p>
        </div>
      </div>
    );
  }

  // Note: We no longer wait for Shopify address - we go directly to split setup
  // The "awaiting_address" status is now just for internal tracking

  // No request found - but don't show error while session is still being created
  if (!pendingRequest && !isCreatingSession && !sessionCreationRef.current && !cartDataParam) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl">Session Not Found</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your split payment session has expired or is invalid. 
              Please start over from the product page.
            </p>
            <Button 
              onClick={() => {
                redirectToMerchantStore(merchantSettings?.store_domain, navigate);
              }} 
              className="w-full"
            >
              Back to Store
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Awaiting payment state - shows while user completes payment in new tab
  if (step === "awaiting_payment") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
            </div>
            <CardTitle className="text-2xl">Waiting for Payment</CardTitle>
            <CardDescription className="text-base">
              Complete your payment in the new tab. This page will update automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{pendingRequest?.product_title}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{participants.length + 1} people splitting {formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Your share: <strong>{formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}</strong></span>
              </div>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p>Once you complete payment, your friends will automatically receive their payment links.</p>
            </div>

            {organizerCheckoutUrl && (
              <Button 
                onClick={() => window.location.href = organizerCheckoutUrl}
                className="w-full"
                variant="outline"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Reopen Payment Page
              </Button>
            )}

            {requestId && (
              <Button 
                onClick={() => navigate(`/split/${requestId}`)} 
                className="w-full"
                variant="ghost"
              >
                View Payment Status
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion state - payment complete
  if (step === "complete") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-950/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
            </div>
            <CardTitle className="text-2xl">Payment Complete! 🎉</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-center text-muted-foreground">
              {GST_MODULE_ENABLED
                ? "Your payment has been submitted successfully. We will raise the final GST invoice only after everyone finishes paying. An email with payment links will be sent to your friends automatically."
                : "Your payment has been submitted successfully. An email with payment links will be sent to your friends automatically."}
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{pendingRequest.product_title}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{participants.length + 1} people splitting {formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Your share: <strong>{formatCurrency(splitAmount, merchantSettings?.currency ?? "USD")}</strong></span>
              </div>
            </div>

            {requestId && (
              <Button 
                onClick={() => navigate(`/split/${requestId}`)} 
                className="w-full"
                variant="outline"
              >
                Track Payment Status
              </Button>
            )}

            <Button 
              onClick={() => {
                redirectToMerchantStore(merchantSettings?.store_domain, navigate);
              }}
              variant="default"
              className="w-full"
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Continue Shopping
            </Button>

            {/* Recurring Reminder Prompt */}
            {isGift && (
              <div className="pt-4 border-t border-border">
                <div className="bg-primary/5 rounded-lg p-4 text-center space-y-3">
                  <Gift className="h-8 w-8 text-primary mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    Is this a recurring occasion like a birthday or anniversary?
                  </p>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReminderDialog(true)}
                  >
                    🔔 Set a Reminder for Next Year
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Recurring Reminder Dialog */}
        <RecurringReminderDialog
          open={showReminderDialog}
          onOpenChange={setShowReminderDialog}
          requestId={requestId || undefined}
          recipientName={`${organizerDetails.firstName} ${organizerDetails.lastName}`.trim()}
          productName={pendingRequest?.product_title}
        />
      </div>
    );
  }

  // Parse shipping address for display
  const getShippingAddressDisplay = () => {
    if (!pendingRequest?.shipping_address) return null;
    try {
      const addr = typeof pendingRequest.shipping_address === 'string' 
        ? JSON.parse(pendingRequest.shipping_address) 
        : pendingRequest.shipping_address;
      return {
        line1: addr.address1,
        line2: addr.address2,
        city: addr.city,
        province: addr.province,
        zip: addr.zip,
        country: addr.country,
      };
    } catch (e) {
      return null;
    }
  };

  const shippingAddress = getShippingAddressDisplay();

  // Use pending request data if available, otherwise fall back to cart URL data
  const displayItems = summaryItems;

  const displayProduct = displayItems?.length ? null : pendingRequest 
    ? {
        title: pendingRequest.product_title,
        image: pendingRequest.product_image,
        quantity: pendingRequest.quantity,
        price: preValidationItemsTotal / Math.max(1, Number(pendingRequest.quantity || 1)),
      }
    : null;

  // Only show full-page loading when we have no data to display at all
  if (!displayProduct && (isCreatingSession || loading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
          <h1 className="text-2xl font-bold">Setting up your split payment...</h1>
          <p className="text-muted-foreground">
            Reserving your product with the store
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              if (step === "address") {
                // Go back to store, not browser history
                redirectToMerchantStore(merchantSettings?.store_domain, navigate);
              } else if (step === "friends") {
                setStep("address");
                window.scrollTo({ top: 0, left: 0, behavior: "instant" });
              } else if (step === "payment") {
                setStep("friends");
                window.scrollTo({ top: 0, left: 0, behavior: "instant" });
              }
            }}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Split Payment</h1>
            <p className="text-muted-foreground">
              {step === "address" && "Step 1: Add delivery address"}
              {step === "friends" && "Step 2: Add friends & split details"}
              {step === "payment" && "Step 3: Complete payment"}
            </p>
          </div>
        </div>

        {/* Progress indicator - 3 steps now */}
        <div className="flex gap-2">
          <div className={`flex-1 h-2 rounded-full ${["address", "friends", "payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
          <div className={`flex-1 h-2 rounded-full ${["friends", "payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
          <div className={`flex-1 h-2 rounded-full ${["payment", "complete"].includes(step) ? "bg-primary" : "bg-muted"}`} />
        </div>

        {/* Order Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {(displayItems?.length ? displayItems : displayProduct ? [displayProduct] : []).map((item, index) => (
                <div key={`${item.title || "item"}-${index}`} className="flex gap-4">
                  {item.image && (
                    <img 
                      src={item.image} 
                      alt={item.title || "Product"}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold">{item.title}</h3>
                    {item.variantTitle && <p className="text-xs text-muted-foreground">{item.variantTitle}</p>}
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                    {Number(item.price || 0) > 0 && (
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(Number(item.price || 0) * Number(item.quantity || 1), displayCurrency)}
                      </p>
                    )}
                    {isCreatingSession && index === 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Reserving with store...</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <Badge variant="secondary" className="text-xs font-medium">
                {taxModeBadgeLabel}
              </Badge>
            </div>

            {/* Pricing breakdown - only show if address validated */}
            {step !== "address" && (
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Items Subtotal</span>
                  <span>{formatCurrency(originalSubtotal, displayCurrency)}</span>
                </div>
                {appliedSplitDiscount.amount > 0 && (
                  <div className="flex justify-between text-sm text-green-700">
                    <span className="text-muted-foreground">
                      Split Discount ({appliedSplitDiscount.percentage}%{appliedSplitDiscount.cap !== null ? `, capped at ${formatCurrency(appliedSplitDiscount.cap, displayCurrency)}` : ""})
                    </span>
                    <span>-{formatCurrency(appliedSplitDiscount.amount, displayCurrency)}</span>
                  </div>
                )}
                {activePricing.shipping > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Truck className="h-3 w-3" /> Shipping
                    </span>
                    <span>{formatCurrency(activePricing.shipping, displayCurrency)}</span>
                  </div>
                )}
                {activePricing.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Tax{inferredTaxMode === "inclusive" ? " (included in item price)" : ""}
                    </span>
                    <span>{formatCurrency(activePricing.tax, displayCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(totalPrice, displayCurrency)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {inferredTaxMode === "inclusive"
                    ? "Store prices already include tax. Shipping is added after address validation based on the selected rate."
                    : inferredTaxMode === "exclusive"
                      ? "Tax is added separately based on the store's tax settings and delivery address."
                      : "Shipping and tax are calculated from the store's live checkout pricing for this address."}
                </p>
                
                {/* Shipping warning only shown on address step, not on payment step */}
              </div>
            )}
            
            {/* Show estimated total on address step */}
            {step === "address" && (
              <div className="border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Items Total</span>
                  <span>{formatCurrency(preValidationItemsTotal, displayCurrency)}</span>
                </div>
                {appliedSplitDiscount.amount > 0 && (
                  <div className="mt-2 flex justify-between text-sm text-green-700">
                    <span className="text-muted-foreground">
                      Split Discount ({appliedSplitDiscount.percentage}%{appliedSplitDiscount.cap !== null ? `, capped at ${formatCurrency(appliedSplitDiscount.cap, displayCurrency)}` : ""})
                    </span>
                    <span>-{formatCurrency(appliedSplitDiscount.amount, displayCurrency)}</span>
                  </div>
                )}
                {appliedSplitDiscount.amount > 0 && (
                  <div className="mt-2 flex justify-between font-medium">
                    <span>Discounted total before shipping/tax</span>
                    <span>{formatCurrency(Math.max(preValidationItemsTotal - appliedSplitDiscount.amount, 0), displayCurrency)}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  * Shipping and final tax treatment will be confirmed after address validation
                </p>
              </div>
            )}

            {/* Shipping address - only show after validation */}
            {step !== "address" && shippingAddress && (
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">Shipping to:</p>
                    <p className="text-muted-foreground">
                      {shippingAddress.line1}
                      {shippingAddress.line2 && `, ${shippingAddress.line2}`}
                      <br />
                      {shippingAddress.city}, {shippingAddress.province} {shippingAddress.zip}
                      <br />
                      {shippingAddress.country}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Address Step - Collect delivery address and validate with Shopify */}
        {step === "address" && (activeSessionToken || isCreatingSession) && (
          <>
            {/* Split Payment Info Banner */}
            <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground mb-1">
                    You're setting up a split payment
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed select-none">
                    <span className="font-medium">1.</span> Add delivery address → <span className="font-medium">2.</span> Add friends → <span className="font-medium">3.</span> Pay your share
                  </p>
                  <a 
                    href="/split-checkout-how-it-works" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                  >
                    How does this work?
                    <span className="text-[10px]">↗</span>
                  </a>
                </div>
              </div>
            </div>

            <ShippingAddressForm
              sessionToken={activeSessionToken}
              onAddressValidated={handleAddressValidated}
              initialEmail={organizerDetails.email}
              isGift={isGift}
              onIsGiftChange={setIsGift}
              giftMessage={organizerGiftMessage}
              onGiftMessageChange={setOrganizerGiftMessage}
              giftRecipientName={giftRecipientName}
              onGiftRecipientNameChange={setGiftRecipientName}
            />
          </>
        )}


        {/* Split Type Selector & Amount Display - shown on friends step */}
        {step === "friends" && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 space-y-4">
              {/* Split Type Toggle */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">How do you want to split?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={splitType === 'equal' ? 'default' : 'outline'}
                    className="w-full justify-start gap-2"
                    onClick={() => setSplitType('equal')}
                  >
                    <Equal className="h-4 w-4" />
                    Split Equally
                  </Button>
                  <Button
                    type="button"
                    variant={splitType === 'custom' ? 'default' : 'outline'}
                    className="w-full justify-start gap-2"
                    onClick={() => setSplitType('custom')}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    Custom Amounts
                  </Button>
                </div>
              </div>
              
              {/* Split Preview */}
              <div className="text-center pt-2 border-t">
                {splitType === 'equal' ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-1">Each person pays</p>
                    <p className="text-3xl font-bold text-primary">{formatCurrency(equalSplitAmount, merchantSettings?.currency ?? "USD")}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Split equally between {participants.length + 1} people
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-1">Total to split</p>
                    <p className="text-3xl font-bold text-primary">{formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Assign amounts below • Remaining: {formatCurrency(Math.max(0, totalPrice - customAmountsTotal), merchantSettings?.currency ?? "USD")}
                    </p>
                    {Math.abs(customAmountsTotal - totalPrice) > 0.01 && customAmountsTotal > 0 && (
                      <p className={`text-sm mt-2 font-medium ${customAmountsTotal > totalPrice ? 'text-destructive' : 'text-amber-600'}`}>
                        {customAmountsTotal > totalPrice 
                          ? `⚠️ Over by ${formatCurrency(customAmountsTotal - totalPrice, merchantSettings?.currency ?? "USD")}`
                          : `${formatCurrency(totalPrice - customAmountsTotal, merchantSettings?.currency ?? "USD")} left to assign`
                        }
                      </p>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {step === "friends" && (
          <>
            {/* Your Details Summary (read-only, from address step) */}
            <Card className="bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  Your Details
                  <Badge variant="secondary" className="ml-auto text-xs">From delivery step</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>{" "}
                    <span className="font-medium">{organizerDetails.firstName && organizerDetails.lastName ? `${organizerDetails.firstName} ${organizerDetails.lastName}` : "Not set"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>{" "}
                    <span className="font-medium">{organizerDetails.email || "Not set"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>{" "}
                    <span className="font-medium">{organizerDetails.phone || "Not set"}</span>
                  </div>
                </div>

                {/* Custom amount for organizer */}
                {splitType === 'custom' && (
                  <div className="mt-3 pt-3 border-t">
                    <Label className="text-sm font-medium mb-2 block">Your share amount *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{getCurrencySymbol(merchantSettings?.currency || 'USD')}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="1"
                        placeholder="Enter your amount"
                        value={organizerCustomAmount || ''}
                        onChange={(e) => setOrganizerCustomAmount(e.target.value ? parseFloat(e.target.value) : undefined)}
                        className="max-w-[150px]"
                      />
                    </div>
                  </div>
                )}
                
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto mt-2 text-xs"
                  onClick={() => {
                    setStep("address");
                    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
                  }}
                >
                  Edit your details
                </Button>
              </CardContent>
            </Card>

            {/* Organizer Message */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Message to Friends (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Add a personal note to your friends, e.g., 'Hey everyone, let's chip in for Sarah's birthday gift!'"
                  value={organizerDetails.message || ""}
                  onChange={(e) => {
                    if (e.target.value.length <= 250) {
                      setOrganizerDetails({...organizerDetails, message: e.target.value});
                    }
                  }}
                  className="resize-none"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground text-right mt-2">
                  {organizerDetails.message?.length || 0}/250 characters
                </p>
              </CardContent>
            </Card>

            {/* Participants */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Friends to Split With ({participants.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {participants.map((participant, index) => (
                  <div key={participant.id} className="space-y-3 p-4 bg-muted/30 rounded-lg relative">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">Friend {index + 1}</Badge>
                        {splitType === 'equal' && (
                          <span className="text-sm text-muted-foreground">
                            pays {formatCurrency(equalSplitAmount, merchantSettings?.currency ?? "USD")}
                          </span>
                        )}
                      </div>
                      {participants.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeParticipant(participant.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid gap-3">
                      <Input
                        placeholder="Name *"
                        value={participant.name}
                        onChange={(e) => updateParticipant(participant.id, "name", e.target.value)}
                      />
                      <Input
                        placeholder="Phone Number (optional)"
                        value={participant.mobile}
                        onChange={(e) => updateParticipant(participant.id, "mobile", e.target.value)}
                      />

                      <Input
                        placeholder="Email *"
                        type="email"
                        value={participant.email}
                        onChange={(e) => updateParticipant(participant.id, "email", e.target.value)}
                      />

                      {/* Custom amount input for this friend */}
                      {splitType === 'custom' && (
                        <div className="flex items-center gap-2 pt-1">
                          <Label className="text-sm whitespace-nowrap">Amount *</Label>
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground text-sm">{getCurrencySymbol(merchantSettings?.currency || 'USD')}</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="1"
                              placeholder="0.00"
                              value={participant.customAmount || ''}
                              onChange={(e) => updateParticipant(participant.id, "customAmount", e.target.value ? parseFloat(e.target.value) : 0)}
                              className="max-w-[120px]"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Add another friend button */}
                {participants.length < 19 && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={addParticipant}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add another friend
                  </Button>
                )}
              </CardContent>
            </Card>


            {/* Split Payment and Cancellation Agreement */}
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="cancellation-agreement"
                    checked={agreedToCancellationFee}
                    onCheckedChange={(checked) => setAgreedToCancellationFee(checked === true)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="cancellation-agreement" className="font-medium cursor-pointer">
                      I understand and agree to the split payment and cancellation terms
                    </Label>
                    <div className="text-sm text-muted-foreground space-y-1.5">
                      <p>
                        Each participant must pay their share online through checkout. Cash on delivery is not accepted for Splitkart split payments.
                      </p>
                      <p>
                        If all participants don't complete payment within{" "}
                        <strong>{getDeadlineLabel(merchantSettings?.default_deadline_hours || 72)}</strong>,
                        the order will be cancelled:
                      </p>
                      <ul className="list-disc list-inside pl-1 space-y-1">
                        <li>
                          A cancellation fee of <strong>{getCancellationFeeDisplay()}</strong> will be deducted from <strong>your (organizer's) payment</strong>
                        </li>
                        <li>
                          All other participants who have paid will receive a <strong>full refund</strong> to their original payment method
                        </li>
                        <li>
                          Refunds are typically processed within 5-10 business days
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Validation Status - Debug helper */}
            {(!isOrganizerValid() || !isParticipantsValid() || !agreedToCancellationFee || (splitType === 'custom' && !isCustomSplitValid)) && (
              <Card className="border-destructive/50 bg-destructive/5">
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-destructive mb-2">Please complete the following:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {!organizerDetails.firstName.trim() && <li>• Enter your first name</li>}
                    {!organizerDetails.lastName.trim() && <li>• Enter your last name</li>}
                    {!organizerDetails.email.trim() && <li>• Enter your email</li>}
                    {organizerDetails.email.trim() && !organizerDetails.email.includes("@") && <li>• Enter a valid email</li>}
                    {!organizerDetails.phone.trim() && <li>• Enter your phone number</li>}
                    {participants.some(p => !p.name.trim() || !p.email.trim() || !p.email.includes("@")) && (
                      <li>• Complete all friend details (name and valid email)</li>
                    )}
                    {hasDuplicatePhones() && (
                      <li className="text-destructive font-medium">• Each person must have a unique phone number - duplicates found</li>
                    )}
                    {hasDuplicateEmails() && (
                      <li className="text-destructive font-medium">• Each person must have a unique email address - duplicates found</li>
                    )}
                    {splitType === 'custom' && !isCustomSplitValid && (
                      <li className="text-destructive font-medium">
                        • Custom split amounts must total exactly {formatCurrency(totalPrice, merchantSettings?.currency ?? "USD")}
                        {customAmountsTotal > 0 && ` (currently ${formatCurrency(customAmountsTotal, merchantSettings?.currency ?? "USD")})`}
                      </li>
                    )}
                    {splitType === 'custom' && (!organizerCustomAmount || organizerCustomAmount <= 0) && (
                      <li>• Enter your share amount</li>
                    )}
                    {splitType === 'custom' && participants.some(p => !p.customAmount || p.customAmount <= 0) && (
                      <li>• Enter amount for each friend</li>
                    )}
                    {!agreedToCancellationFee && <li>• Agree to the split payment and cancellation terms</li>}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* You're One Step Away - Payment Preview Section */}
            {isOrganizerValid() && isParticipantsValid() && agreedToCancellationFee && (splitType === 'equal' || isCustomSplitValid) && (
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                    You're One Step Away!
                  </CardTitle>
                  <CardDescription>
                    Complete your payment to send invitations to your friends
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-2">
                    <p className="text-muted-foreground text-sm mb-1">Your share amount</p>
                    <p className="text-3xl font-bold text-primary">{formatCurrency(organizerAmount, merchantSettings?.currency ?? "USD")}</p>
                  </div>

                  <div className="bg-background border rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">How it works:</strong> You will be redirected to the merchant's checkout to complete your payment. 
                      Once paid, your friends will automatically receive email invitations with their secure payment links.
                    </p>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium">Payment invitations will be sent to:</p>
                    <ul className="space-y-1">
                      {participants.map((p) => (
                        <li key={p.id} className="text-sm flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span>{p.name} ({p.email})</span>
                          </div>
                          <span className="text-muted-foreground font-medium">
                            {formatCurrency(splitType === 'custom' ? (p.customAmount || 0) : equalSplitAmount, merchantSettings?.currency ?? "USD")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Continue Button */}
            <Button
              className="w-full"
              size="lg"
              onClick={handleCreateRequest}
              disabled={isSending || !isOrganizerValid() || !isParticipantsValid() || !agreedToCancellationFee || (splitType === 'custom' && !isCustomSplitValid)}
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up payment...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay {formatCurrency(organizerAmount, merchantSettings?.currency ?? "USD")} Now
                </>
              )}
            </Button>
          </>
        )}

        {step === "payment" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                You're One Step Away!
              </CardTitle>
              <CardDescription>
                Complete your payment to send invitations to your friends
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-2">Your share amount</p>
                <p className="text-4xl font-bold text-primary">{formatCurrency(organizerAmount, merchantSettings?.currency ?? "USD")}</p>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  <strong>How it works:</strong> You will be redirected to the merchant's checkout to complete your payment. 
                  Once paid, your friends will automatically receive email invitations with their secure payment links.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium">Payment invitations will be sent to:</p>
                <ul className="space-y-1">
                  {participants.map((p) => (
                    <li key={p.id} className="text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span>{p.name} ({p.email})</span>
                      </div>
                      <span className="text-muted-foreground font-medium">
                        {formatCurrency(splitType === 'custom' ? (p.customAmount || 0) : equalSplitAmount, merchantSettings?.currency ?? "USD")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                className="w-full"
                size="lg"
                onClick={handleOrganizerPayment}
                disabled={isProcessingPayment}
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating checkout...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay {formatCurrency(organizerAmount, merchantSettings?.currency ?? "USD")} via Merchant Checkout
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ShakatahSplitSetup;
